import { type FC, useRef } from 'react'
import { useRevalidator } from 'react-router-dom'

import { Button } from '@app/components/ui/button'
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@app/components/ui/dialog'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import { useMetricDeleteAllMetricDataPointsMutation } from '@graphql/queries'

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  metric: {
    id: string
  }
}

const MetricDataPointsClearButton: FC<Props> = ({ metric, open, setOpen }) => {
  const { user } = useStoreCurrentUser()
  const revalidator = useRevalidator()
  const cancelRef = useRef(null)
  const [, deleteDataPoints] = useMetricDeleteAllMetricDataPointsMutation()

  const { id } = metric

  if (!user) {
    return null
  }

  const onSubmit = () =>
    deleteDataPoints({ input: { metricId: id } }).then(() => {
      setOpen(false)
      revalidator.revalidate()
    })

  return (
    <>
      <DialogRoot
        initialFocusEl={cancelRef?.current}
        onOpenChange={(e) => setOpen(e.open)}
        open={open}
        placement="center"
        role="alertdialog"
      >
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear data points</DialogTitle>
          </DialogHeader>

          <DialogBody>
            Are you sure you want to delete all the data for this metric? This can&apos;t be undone.
          </DialogBody>

          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button ref={cancelRef} variant="outline">
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button colorPalette="red" onClick={onSubmit}>
              OK
            </Button>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  )
}

export default MetricDataPointsClearButton
